import type { PinVerdict } from "@/data/pin-verdicts";

/**
 * How the portal words the app's verdict on a pinned colour (`pinned`,
 * product#116). The reason is the app's own, verbatim — the rule lives in the
 * app, never here.
 */

/** One line for a refused pin; null for a placed one (nothing to say). */
export function refusedPinNotice(verdict: PinVerdict, colour: string): string | null {
  if (verdict.placed) return null;
  const reason = verdict.reason.replace(/\.+$/, "");
  return `${colour} is not held: ${reason}. The place uses pop.in’s own colour for this style.`;
}

/** The note on publish when a refused pin was cleared rather than saved. */
export function notSavedNote(cleared: readonly PinVerdict["pin"][]): string {
  const brand = cleared.includes("primary");
  const accent = cleared.includes("accent");
  const what = brand && accent ? "Your brand and accent colours were" : brand ? "Your brand colour was" : "Your accent colour was";
  return `${what} not saved; the place uses pop.in’s colour.`;
}

/** The note on publish when the app hadn't answered on the draft's colour yet. */
export function notJudgedNote(): string {
  return "The preview hadn’t checked your latest colour yet, so it was saved as picked.";
}

/**
 * The app's verdicts under the colour picker. We send no `pins`, so the app pins
 * the recipe seed as `primary` — hence `seed` names that pin. Nothing shows for a
 * placed pin, or before the app has answered.
 */
export function PinVerdictNotice({ verdicts, seed }: { verdicts: readonly PinVerdict[]; seed: string }) {
  const lines = verdicts
    .map((v) => refusedPinNotice(v, v.pin === "primary" ? seed.toUpperCase() : "Your accent colour"))
    .filter((line): line is string => line !== null);
  if (!lines.length) return null;
  return (
    <>
      {lines.map((line) => (
        <p key={line} role="status" className="text-xs text-foreground">
          {line}
        </p>
      ))}
    </>
  );
}
